import { Popover } from "@base-ui/react/popover";
import type React from "react";
import { forwardRef, useEffect, useState } from "react";
import * as styles from "./styles.css";

const KEYBOARD_LAYOUTS = {
  default: [
    ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
    ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
    ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
    ["z", "x", "c", "v", "b", "n", "m"],
  ],
  numeric: [["1", "2", "3"], ["4", "5", "6"], ["7", "8", "9"], ["0"]],
};

export const Keyboard = forwardRef(function Keyboard(
  props: React.HTMLAttributes<HTMLDivElement>,
  ref: React.Ref<HTMLDivElement>,
) {
  const [visible, setVisible] = useState(false);
  const [focusedInput, setFocusedInput] = useState<HTMLInputElement | null>(
    null,
  );
  const [keyboardType, setKeyboardType] = useState("default");

  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLInputElement;
      if (target.tagName === "INPUT") {
        setFocusedInput(target);
        setKeyboardType(target.getAttribute("keyboard-type") || "default");
        setVisible(true);
      }
    };

    const handleFocusOut = (event: FocusEvent) => {
      if (
        !(event.relatedTarget as HTMLElement)?.classList.contains(
          "keyboard-button",
        )
      ) {
        setVisible(false);
        setFocusedInput(null);
      }
    };

    window.addEventListener("focusin", handleFocusIn);
    window.addEventListener("focusout", handleFocusOut);

    return () => {
      window.removeEventListener("focusin", handleFocusIn);
      window.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  const handleKeyPress = (
    key: string,
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    if (focusedInput) {
      const start = focusedInput.selectionStart || 0;
      const end = focusedInput.selectionEnd || 0;
      const value = focusedInput.value;

      if (key === "Backspace") {
        focusedInput.value = value.slice(0, start - 1) + value.slice(end);
        focusedInput.setSelectionRange(start - 1, start - 1);
        focusedInput.dispatchEvent(new Event("input", { bubbles: true }));
        return;
      }
      focusedInput.value = value.slice(0, start) + key + value.slice(end);
      focusedInput.setSelectionRange(start + 1, start + 1);
      focusedInput.dispatchEvent(new Event("input", { bubbles: true }));
    }
  };

  return (
    <Popover.Root open={visible} onOpenChange={setVisible}>
      <Popover.Portal>
        <Popover.Positioner
          anchor={focusedInput}
          side="bottom"
          align="start"
          sideOffset={4}
          className={styles.keyboardPositioner}
        >
          <Popover.Popup
            initialFocus={false}
            finalFocus={false}
            ref={ref}
            {...props}
            className={styles.keyboardContainer}
          >
            {KEYBOARD_LAYOUTS[
              keyboardType as keyof typeof KEYBOARD_LAYOUTS
            ].map((row) => {
              return (
                <div key={row.join("-")} className={styles.keyboardRow}>
                  {row.map((key) => (
                    <button
                      className={`${styles.keyButton} keyboard-button`}
                      key={key}
                      onMouseDown={(e) => handleKeyPress(key, e)}
                    >
                      {key}
                    </button>
                  ))}
                </div>
              );
            })}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
});

Keyboard.displayName = "Keyboard";
